import { ArrowTriangleDownIconRaw } from '@tableau/icons/ArrowTriangleDownIcon/raw';
import { TabStyles } from '@tableau/widgets';
import { CSSProperties } from 'react';
import { cssRule } from 'typestyle';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

/*
 * We need normal CSS to override TinyMCE's styling
 */
const defaultFont: CSSProperties = {
  fontFamily: `${CommonStyles.Typography.FontFamily} !important`,
  fontSize: TabStyles.Typography.Sizing.Default,
};

// Note: Using !important here so :focus overrides the :hover style.
// It's possible that typestyle doesn't keep the order of the CSS,
// so this is a failsafe. !important is a one-off here.
const focusStyle = {
  border: `1px solid ${CommonStyles.Colors.FocusBorderBlue} !important`,
  'box-shadow': `0 0 4px 0 ${CommonStyles.Colors.FocusBorderBlue} !important`
};

/**
 * This is not ideal to duplicate button styles, but we want to join
 * forces with the widgets team on finding a long-term solution.
 */
const baseButtonStyle = {
  ...defaultFont,
  height: '24px',
  backgroundColor: 'transparent',
  display: 'inline-block',
  border: '1px solid #666',
  borderRadius: 2,
  lineHeight: '22px',
  padding: '0 24px',
  color: '#666',
  outline: 'none'
};

const buttonStyle = {
  primary: {
    ...baseButtonStyle,
    backgroundColor: '#1a699e',
    borderColor: '#1a699e',
    color: '#fff',
    $nest: {
      '&:hover': {
        backgroundColor: '#2a79af',
        borderColor: '#2a79af'
      },
      '&:active': {
        backgroundColor: '#15557f',
        borderColor: '#15557f'
      },
      '&:focus': {
        ...focusStyle
      }
    }
  },
  outline: {
    ...baseButtonStyle,
    $nest: {
      '&:hover': {
        backgroundColor: '#ebebeb'
      },
      '&:active': {
        backgroundcolor: '#ccc',
        borderColor: '#333',
        color: '#333',
      },
      '&:focus': {
        ...focusStyle
      }
    }
  }
};

/**
 * Tooltip popup
 */

// Proper container height for the popup
cssRule('.tox-pop', {
  maxHeight: '50px !important'  /* TinyMCE applies max-height via style attribute */
});

// Popup textfield
cssRule('.tox-pop .tox-toolbar-textfield', {
  ...defaultFont
});

/**
 * Right click menu popup
 */

cssRule('.tox-menu .tox-collection__item-label', {
  ...defaultFont,
});

/**
 * Dialog Styling
 */

cssRule('.tox-dialog .tox-dialog__body-content', {
  padding: '12px 18px'
});

cssRule('.tox-dialog .tox-dialog__header', {
  backgroundColor: TabStyles.Colors.F2,
  height: '30px',
  padding: '0 18px'
});

cssRule('.tox-dialog .tox-dialog__title', {
  color: TabStyles.Colors.Black,
  ...defaultFont,
  lineHeight: 'inherit'
});

cssRule('.tox-dialog .tox-label', {
  ...defaultFont,
  lineHeight: '19px'
});

cssRule('.tox-dialog .tox-textfield', {
  ...generateInputFieldStyle(),
  marginBottom: '12px'
});

cssRule('.tox-dialog .tox-selectfield select', {
  ...generateInputFieldStyle()
});

cssRule('.tox-dialog .tox-selectfield__icon-js svg', {
  height: 18,
  width: 18,
  backgroundImage: `url(${ArrowTriangleDownIconRaw(18, CommonStyles.Colors.Grey102)})`,
  $nest: {
    'path': {
      display: 'none'
    }
  }
});

// Intentionally hide TinyMCE's "Open Link In..." dropdown
// since we don't respect it when converting to markdown.
cssRule('.tox-dialog .tox-form .tox-form__group:nth-of-type(4)', {
  display: 'none'
});

function generateInputFieldStyle() {
  return {
    border: '1px solid transparent',
    borderBottom: `1px solid ${TabStyles.Colors.L6}`,
    borderRadius: '1px 1px 0 0',
    ...defaultFont,
    lineHeight: 'inherit',
    padding: '0px 7px',
    height: '24px',
    $nest: {
      '&:hover': {
        borderBottomColor: TabStyles.Colors.L8,
        backgroundColor: '#fafafa'
      },
      '&:focus': {
        backgroundColor: 'transparent !important',
        ...focusStyle
      }
    }
  };
}

// Close button
cssRule('.tox-dialog .tox-button--naked', {
  display: 'none !important',
});

// Ok button
cssRule('.tox .tox-dialog .tox-button', {
  ...buttonStyle.primary
});

// Cancel button
cssRule('.tox .tox-dialog .tox-button.tox-button--secondary', {
  ...buttonStyle.outline
});

cssRule('.tox .tox-dialog__footer', {
  border: 'none',
  padding: '0 18px',
  paddingBottom: '12px'
});

cssRule('.mce-content-body [data-mce-selected=inline-boundary]', {
  backgroundColor: 'none'
});

